import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, Button, Spinner } from '@tkpod/ui';
import { api } from '@/lib/api';
import { CategoryTree, type CategoryNode } from '@/components/catalog/CategoryTree';
import { CategoryFormModal } from '@/components/catalog/CategoryFormModal';

interface CategoriesResponse {
  data: CategoryNode[];
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (!err || typeof err !== 'object') return fallback;

  const e = err as { message?: string; statusCode?: number; isNetwork?: boolean };

  if (e.isNetwork || e.statusCode === 0) {
    return 'Could not reach the server. Please check if the API is running.';
  }

  const msg = e.message ?? '';

  if (!msg || msg.startsWith('Cannot ') || msg.includes('/api/') || msg.includes('fetch')) {
    return fallback;
  }

  return msg;
}

export function CatalogCategoriesPage() {
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState(false);
  const [editCategory, setEditCategory] = useState<CategoryNode | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<CategoryNode | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CategoryNode | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data, isLoading } = useQuery<CategoriesResponse>({
    queryKey: ['catalog-categories-tree'],
    queryFn: () =>
      api.get<CategoriesResponse>('/api/v1/admin/catalog/categories', {
        query: { tree: 'true' },
      }),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/admin/catalog/categories/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog-categories-tree'] });
      queryClient.invalidateQueries({ queryKey: ['catalog-categories'] });
      setArchiveTarget(null);
      setArchiveError(null);
    },
    onError: (err: Error) => {
      setArchiveError(extractErrorMessage(err, 'Failed to archive category. Please try again.'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/admin/catalog/categories/${id}/permanent`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog-categories-tree'] });
      queryClient.invalidateQueries({ queryKey: ['catalog-categories'] });
      setDeleteTarget(null);
      setDeleteError(null);
    },
    onError: (err: Error) => {
      setDeleteError(extractErrorMessage(err, 'Failed to delete category. Please try again.'));
    },
  });

  const categories = data?.data ?? [];

  const openCreate = () => {
    setEditCategory(null);
    setModalOpen(true);
  };

  const openEdit = (cat: CategoryNode) => {
    setEditCategory(cat);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditCategory(null);
  };

  const handleArchiveConfirm = () => {
    if (!archiveTarget) return;
    setArchiveError(null);
    archiveMutation.mutate(archiveTarget.id);
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    deleteMutation.mutate(deleteTarget.id);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Product Categories"
          description="Organize your catalog with hierarchical categories."
          action={
            <Button size="sm" onClick={openCreate}>
              + Category
            </Button>
          }
        />

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-surface-secondary">
              <svg
                className="h-6 w-6 text-text-tertiary"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"
                />
              </svg>
            </div>
            <p className="mt-3 text-sm font-medium text-text-primary">No categories yet</p>
            <p className="mt-1 text-sm text-text-tertiary">
              Create your first product category to organize your catalog.
            </p>
            <Button size="sm" className="mt-4" onClick={openCreate}>
              + Create Category
            </Button>
          </div>
        ) : (
          <CategoryTree
            categories={categories}
            onEdit={openEdit}
            onArchive={setArchiveTarget}
            onDelete={setDeleteTarget}
          />
        )}
      </Card>

      {/* Create / Edit Modal */}
      <CategoryFormModal
        open={modalOpen}
        onClose={closeModal}
        category={editCategory}
        allCategories={categories}
      />

      {/* Archive Confirmation Dialog */}
      {archiveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/40"
            onClick={() => !archiveMutation.isPending && setArchiveTarget(null)}
          />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-surface p-6 shadow-xl">
            <div className="mb-1 flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-5 w-5 text-amber-600"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-text-primary">Archive Category</h3>
            </div>

            <p className="mt-3 text-sm text-text-secondary">
              Are you sure you want to archive{' '}
              <strong className="text-text-primary">{archiveTarget.name}</strong>?
              {archiveTarget.templateCount > 0 && (
                <>
                  {' '}
                  This category has{' '}
                  <strong className="text-text-primary">
                    {archiveTarget.templateCount} template
                    {archiveTarget.templateCount !== 1 ? 's' : ''}
                  </strong>{' '}
                  assigned to it. They will need to be reassigned.
                </>
              )}
              {archiveTarget.childCount > 0 && (
                <>
                  {' '}
                  It also has{' '}
                  <strong className="text-text-primary">
                    {archiveTarget.childCount} sub-categor
                    {archiveTarget.childCount !== 1 ? 'ies' : 'y'}
                  </strong>{' '}
                  that may be affected.
                </>
              )}
            </p>

            {archiveError && (
              <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-danger-200 bg-danger-50 p-3">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 shrink-0 text-danger-500 mt-px">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-danger-700">{archiveError}</p>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-3">
              <Button
                size="sm"
                variant="outline"
                disabled={archiveMutation.isPending}
                onClick={() => {
                  setArchiveTarget(null);
                  setArchiveError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="primary"
                loading={archiveMutation.isPending}
                onClick={handleArchiveConfirm}
                className="!bg-amber-600 hover:!bg-amber-700"
              >
                {archiveMutation.isPending ? 'Archiving...' : 'Yes, Archive'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/40"
            onClick={() => !deleteMutation.isPending && setDeleteTarget(null)}
          />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-surface p-6 shadow-xl">
            <div className="mb-1 flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger-100">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-5 w-5 text-danger-600"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-text-primary">Delete Category</h3>
            </div>

            <p className="mt-3 text-sm text-text-secondary">
              Are you sure you want to permanently delete{' '}
              <strong className="text-text-primary">{deleteTarget.name}</strong>?
              This action cannot be undone.
            </p>

            {(deleteTarget.templateCount > 0 || deleteTarget.childCount > 0) && !deleteError && (
              <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 shrink-0 text-amber-500 mt-px">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-amber-800">
                  {deleteTarget.templateCount > 0 && (
                    <>
                      This category has{' '}
                      <strong>{deleteTarget.templateCount} template{deleteTarget.templateCount !== 1 ? 's' : ''}</strong> assigned.
                    </>
                  )}
                  {deleteTarget.childCount > 0 && (
                    <>
                      {deleteTarget.templateCount > 0 ? ' It also has ' : 'This category has '}
                      <strong>{deleteTarget.childCount} sub-categor{deleteTarget.childCount !== 1 ? 'ies' : 'y'}</strong>.
                    </>
                  )}
                  {' '}Remove or reassign them before deleting.
                </p>
              </div>
            )}

            {deleteError && (
              <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-danger-200 bg-danger-50 p-3">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 shrink-0 text-danger-500 mt-px">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-danger-700">{deleteError}</p>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-3">
              <Button
                size="sm"
                variant="outline"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  setDeleteTarget(null);
                  setDeleteError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="danger"
                loading={deleteMutation.isPending}
                onClick={handleDeleteConfirm}
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Yes, Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
